import { useCallback, useEffect, useState } from 'react';
import type { IPreviouslyUnlinkedRow } from '@skystone/shared';
import { personApi } from '@/services/person-api';
import { getErrorMessage } from '@/lib/utils';
import { Badge, ErrorBanner } from '@/components/ui';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { LinkToPersonDialog } from './LinkToPersonDialog';
import type { ReconcileTarget } from './reconcile-target';

interface PreviouslyUnlinkedTabProps {
  onActionCompleted?: () => void;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString();
}

export function PreviouslyUnlinkedTab({ onActionCompleted }: Readonly<PreviouslyUnlinkedTabProps>) {
  const [rows, setRows] = useState<IPreviouslyUnlinkedRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [linkTarget, setLinkTarget] = useState<ReconcileTarget | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const response = await personApi.listPreviouslyUnlinked();
      setRows(response);
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to load previously unlinked rows'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleRelink = (row: IPreviouslyUnlinkedRow): void => {
    if (row.source !== 'slack' && row.source !== 'github') {
      setError('Re-link for AD accounts is not yet supported in this view.');
      return;
    }
    setLinkTarget({
      source: row.source,
      sourceAccountId: row.sourceAccountId,
      displayName: row.displayName ?? row.sourceAccountId,
      email: null,
      isBot: false,
    });
  };

  const handleRelinkCompleted = async (): Promise<void> => {
    setLinkTarget(null);
    await load();
    onActionCompleted?.();
  };

  return (
    <div className="space-y-4">
      <ErrorBanner message={error} />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <span>Previously unlinked</span>
            <span
              className="inline-flex min-w-[1.5rem] items-center justify-center rounded-full bg-muted px-2 text-xs font-semibold"
              data-testid="previously-unlinked-count"
            >
              {rows.length}
            </span>
          </CardTitle>
          <CardDescription>
            Accounts that were explicitly unlinked. Auto-match will not re-link them. Use this
            view to find and re-link them to the right Person.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {rows.length === 0 && !loading && (
            <p className="text-sm text-muted-foreground">No previously unlinked accounts.</p>
          )}
          {rows.map((row) => (
            <div
              key={row.linkId}
              className="flex flex-wrap items-center justify-between gap-3 border-t py-3 first:border-t-0 first:pt-0"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">{row.source}</Badge>
                  <span className="truncate font-medium text-foreground">
                    {row.displayName ?? row.sourceAccountId}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground">
                  <span className="mr-2 font-mono">{row.sourceAccountId}</span>
                  <span>unlinked {formatDate(row.updatedAt)}</span>
                </div>
              </div>
              <Button size="sm" onClick={() => handleRelink(row)}>Re-link to Person</Button>
            </div>
          ))}
        </CardContent>
      </Card>

      <LinkToPersonDialog
        open={linkTarget !== null}
        target={linkTarget}
        onClose={() => setLinkTarget(null)}
        onConfirmed={handleRelinkCompleted}
      />
    </div>
  );
}
